import React, { useMemo } from 'react';
import { IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import BaseCard from './BaseCard';
import BaseQuestionModeCard from './BaseQuestionModeCard';
import CardSkeleton from './CardSkeleton';
import styles from './questionCard.module.scss';
import PromptOnlyQuestionCard from './PromptOnlyQuestionCard';
import useQuestionCardData from './useQuestionCardData';
import {
  DragAndDropQuestionStudentAnswer,
  DrawingQuestionStudentAnswer,
  FillInTheBlankQuestionStudentAnswer,
  GridQuestionStudentAnswer,
  ImageMultipleChoiceQuestionStudentAnswer,
  MultipleChoiceQuestionStudentAnswer,
  SliderQuestionStudentAnswer,
  WritingQuestionStudentAnswer,
} from '../StudentAnswers';

const STUDENT_ANSWER_COMPONENT_MAP = {
  DragAndDropQuestion: DragAndDropQuestionStudentAnswer,
  DrawingQuestion: DrawingQuestionStudentAnswer,
  FillInTheBlankQuestion: FillInTheBlankQuestionStudentAnswer,
  GridQuestion: GridQuestionStudentAnswer,
  ImageMultipleChoiceQuestion: ImageMultipleChoiceQuestionStudentAnswer,
  MultipleChoiceQuestion: MultipleChoiceQuestionStudentAnswer,
  OpenEndedQuestion: WritingQuestionStudentAnswer,
  SliderQuestion: SliderQuestionStudentAnswer,
};

interface QuestionCardProps {
  position?: number;
  question: IQuestion;
  questionAnswer?: IQuestionAnswer;
}

const QuestionCard = ({ position, question, questionAnswer }: QuestionCardProps) => {
  const { isQuestionMode } = useGradingStore();
  const {
    answerKeyHtml,
    isLoading: isCardDataLoading,
    rubricData,
    sectionTitle,
    stimuli,
  } = useQuestionCardData(question, typeof position === 'number');

  // Memoize StudentAnswer to prevent component from unmounting/remounting when only score changes
  // Only recreate when question.id or questionAnswer.id changes (not when score/comment changes)
  const StudentAnswer = useMemo(() => {
    const Component = STUDENT_ANSWER_COMPONENT_MAP[question.type];
    if (Component) {
      return (
        <Component
          question={question}
          questionAnswer={questionAnswer}
        />
      );
    }

    return <div key={question.id}>Student answer not available</div>;
  }, [question.id, questionAnswer?.id]);

  if (question.type === 'PromptOnly' && !isQuestionMode) {
    return <PromptOnlyQuestionCard question={question} />;
  }

  const QuestionContent = () => (
    <div className={styles.bodyContent}>
      <div dangerouslySetInnerHTML={{ __html: question.sanitized_body }} />
    </div>
  );

  if (isCardDataLoading) {
    return <CardSkeleton />;
  }

  if (isQuestionMode) {
    return (
      <BaseQuestionModeCard
        answerKeyHtml={answerKeyHtml}
        question={question}
        rubricData={rubricData}
        stimuli={stimuli}
      >
        <QuestionContent />
      </BaseQuestionModeCard>
    );
  }

  return (
    <BaseCard
      key={question.id}
      answerKeyHtml={answerKeyHtml}
      position={position}
      question={question}
      questionAnswer={questionAnswer}
      rubricData={rubricData}
      sectionTitle={sectionTitle}
      stimuli={stimuli}
      studentAnswerHtml={StudentAnswer}
    >
      <QuestionContent />
    </BaseCard>
  );
};

export default QuestionCard;
